import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Zap, Server } from 'lucide-react';
import api from '../../services/api';

export default function GeneralSettings() {
    const [settings, setSettings] = useState({
        dashboard_update_mode: 'polling', // polling | websocket
        dashboard_polling_interval: '30000' // ms
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const response = await api.get('/settings');
            // Mapear array de chave/valor para objeto
            const settingsMap = {};
            response.data.forEach(s => settingsMap[s.key] = s.value);

            setSettings(prev => ({
                ...prev,
                ...settingsMap
            }));
        } catch (error) {
            console.error('Erro ao carregar configurações gerais:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            // Salvar cada configuração individualmente (API atual suporta PUT /:key)
            await Promise.all([
                api.put('/settings/dashboard_update_mode', { value: settings.dashboard_update_mode }),
                api.put('/settings/dashboard_polling_interval', { value: settings.dashboard_polling_interval })
            ]);
            alert('Configurações salvas com sucesso!');
        } catch (error) {
            console.error('Erro ao salvar:', error);
            alert('Erro ao salvar configurações.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center"><div className="spinner-border text-primary"></div></div>;

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    Comportamento do Dashboard
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Modo de Atualização */}
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                            Modo de Atualização (Smart Refresh)
                        </label>
                        <div className="grid grid-cols-1 gap-3">
                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${settings.dashboard_update_mode === 'polling' ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-300' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                                <input
                                    type="radio"
                                    name="mode"
                                    value="polling"
                                    checked={settings.dashboard_update_mode === 'polling'}
                                    onChange={(e) => handleChange('dashboard_update_mode', e.target.value)}
                                    className="h-4 w-4 text-blue-600"
                                />
                                <div>
                                    <div className="font-bold text-sm text-gray-800">Padrão (Polling)</div>
                                    <div className="text-xs text-gray-500">Verifica atualizações periodicamente. Menor consumo de servidor.</div>
                                </div>
                                <RefreshCw className="ml-auto w-5 h-5 text-gray-400" />
                            </label>

                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${settings.dashboard_update_mode === 'websocket' ? 'bg-purple-50 border-purple-200 ring-1 ring-purple-300' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                                <input
                                    type="radio"
                                    name="mode"
                                    value="websocket"
                                    checked={settings.dashboard_update_mode === 'websocket'}
                                    onChange={(e) => handleChange('dashboard_update_mode', e.target.value)}
                                    className="h-4 w-4 text-purple-600"
                                />
                                <div>
                                    <div className="font-bold text-sm text-gray-800">Tempo Real (WebSocket)</div>
                                    <div className="text-xs text-gray-500 flex items-center gap-1">
                                        <Zap size={12} className="text-yellow-500 fill-yellow-500" />
                                        Atualização instantânea ("Live"). Recomendado para alta demanda.
                                    </div>
                                </div>
                                <Server className="ml-auto w-5 h-5 text-gray-400" />
                            </label>
                        </div>
                    </div>

                    {/* Intervalo (Apenas para Polling) */}
                    <div className={`space-y-3 transition-opacity ${settings.dashboard_update_mode === 'websocket' ? 'opacity-50 pointer-events-none' : ''}`}>
                        <label className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                            Intervalo de Verificação (Polling)
                        </label>
                        <select
                            value={settings.dashboard_polling_interval}
                            onChange={(e) => handleChange('dashboard_polling_interval', e.target.value)}
                            className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                        >
                            <option value="10000">10 Segundos (Rápido)</option>
                            <option value="30000">30 Segundos (Padrão)</option>
                            <option value="60000">1 Minuto (Econômico)</option>
                            <option value="120000">2 Minutos (Muito Econômico)</option>
                        </select>
                        <p className="text-xs text-gray-500">
                            Frequência que o sistema verifica alterações no banco de dados.
                        </p>
                    </div>
                </div>

                <div className="mt-8 flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold shadow-lg shadow-blue-200 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {saving ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                Salvar Configurações
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
